import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Bell, 
  CheckCheck, 
  Info, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldAlert, 
  ExternalLink,
  Layers
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthState } from "react-redux-django-auth";
import Button from "../common/Button";
import { 
  fetchNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  fetchUnreadCount,
  NotificationItem, 
  NotificationType 
} from "../../services/notificationService";

const getRelativeTime = (dateInput?: string | null): string => {
  if (!dateInput) return "";
  try {
    const now = new Date();
    const date = new Date(dateInput);
    const diffMs = now.getTime() - date.getTime();

    if (diffMs < 60000) return "just now";
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.round(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.round(diffMs / 86400000);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const getTypeConfig = (type: NotificationType | string) => {
  switch (type?.toUpperCase()) {
    case "SUCCESS":
      return {
        icon: <CheckCircle2 size={16} />,
        badgeBg: "cht-bg-emerald-50 cht-text-emerald-700",
        iconContainer: "cht-bg-emerald-100 cht-text-emerald-600",
        label: "Success",
      };
    case "WARNING":
      return {
        icon: <AlertTriangle size={16} />,
        badgeBg: "cht-bg-amber-50 cht-text-amber-700",
        iconContainer: "cht-bg-amber-100 cht-text-amber-600",
        label: "Warning",
      };
    case "ALERT":
      return {
        icon: <ShieldAlert size={16} />,
        badgeBg: "cht-bg-rose-50 cht-text-rose-700",
        iconContainer: "cht-bg-rose-100 cht-text-rose-600",
        label: "Alert",
      };
    case "INFO":
    default:
      return {
        icon: <Info size={16} />,
        badgeBg: "cht-bg-blue-50 cht-text-blue-700",
        iconContainer: "cht-bg-blue-100 cht-text-blue-600",
        label: "Info",
      };
  }
};

interface NotificationItemRowProps {
  item: NotificationItem;
  onMarkRead: (id: number) => void;
  onNavigate: (route?: string) => void;
}

const NotificationItemRow: React.FC<NotificationItemRowProps> = ({ item, onMarkRead, onNavigate }) => {
  const config = getTypeConfig(item.notification_type);
  const isUnread = !item.is_read;
  const timeLabel = getRelativeTime(item.created_at);

  const handleClick = () => {
    if (isUnread) {
      onMarkRead(item.id);
    }
    const route = item.extra_data?.route || item.extra_data?.link || item.extra_data?.url;
    if (route) {
      onNavigate(route);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`cht-group cht-flex cht-items-start cht-p-3.5 cht-rounded-2xl cht-transition-all cht-duration-200 hover:cht-bg-gray-50/90 cht-cursor-pointer cht-border cht-border-transparent hover:cht-border-gray-100 ${
        isUnread ? "cht-bg-[#3e57da]/[0.03] cht-border-[#3e57da]/10" : ""
      }`}
    >
      {/* Icon */}
      <div
        className={`cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-shrink-0 ${config.iconContainer} cht-shadow-sm cht-mt-0.5`}
      >
        {config.icon}
      </div>

      {/* Details */}
      <div className="cht-ml-3.5 cht-flex-1 cht-min-w-0">
        <div className="cht-flex cht-items-start cht-justify-between cht-gap-2">
          <h5 className="cht-text-xs cht-font-black cht-text-gray-900 cht-leading-snug cht-truncate">
            {item.title}
          </h5>
          <div className="cht-flex cht-items-center cht-gap-1 cht-shrink-0">
            <span className="cht-text-[9px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">
              {timeLabel}
            </span>
            {!isUnread && (
              <CheckCheck size={12} className="cht-text-[#3e57da] cht-stroke-[2.5]" />
            )}
          </div>
        </div>

        <p className="cht-text-[11px] cht-text-gray-500 cht-mt-1 cht-line-clamp-2 cht-font-medium cht-leading-relaxed">
          {item.message}
        </p>

        {/* Target or Actor Pills */}
        <div className="cht-flex cht-items-center cht-gap-2 cht-mt-2.5">
          {item.target_model && (
            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-gray-600 cht-rounded-md cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider">
              <Layers size={9} />
              {item.target_model}
              {item.target_id ? ` #${item.target_id}` : ""}
            </span>
          )}

          {item.actor_name && (
            <span className="cht-text-[9px] cht-font-semibold cht-text-gray-400">
              by {item.actor_name}
            </span>
          )}

          {isUnread && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(item.id);
              }}
              className="cht-ml-auto cht-text-[10px] cht-font-black cht-text-[#3e57da] hover:cht-underline"
            >
              Mark read
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const UniversalNotificationDropdown: React.FC = () => {
  const { user: rawUser } = useAuthState();
  const navigate = useNavigate();
  const user = (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Dynamic role-based destination for "View All"
  const resolvedAllUrl = (() => {
    const role = (user?.role || "").toLowerCase();
    if (user?.is_superuser || role === "superadmin") return "/superadmin/dashboard/notifications";
    if (user?.is_staff || role === "admin") return "/staff/dashboard/notifications";
    if (role === "instructor") return "/instructor/dashboard/notifications";
    return "/student/dashboard/notifications";
  })();

  // Poll unread count
  const checkUnreadCount = useCallback(async () => {
    try {
      const count = await fetchUnreadCount();
      setUnreadCount(count);
    } catch {
      // ignore network errors during background sync
    }
  }, []);

  // Load preview list
  const loadRecentNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchNotifications({ page: 1, page_size: 5 });
      setNotifications(res.results || []);
    } catch (err) {
      console.error("Failed to load notifications dropdown:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check unread count once on mount if user is authenticated (NO continuous interval polling)
    if (user) {
      checkUnreadCount();
    }
  }, [user, checkUnreadCount]);

  // Handle outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  const handleToggle = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (nextState) {
      loadRecentNotifications();
      checkUnreadCount();
    }
  };

  const handleMarkRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const handleNavigate = (route?: string) => {
    setIsOpen(false);
    if (route) {
      if (route.startsWith("http://") || route.startsWith("https://")) {
        window.open(route, "_blank");
      } else {
        navigate(route);
      }
    }
  };

  return (
    <div className="cht-relative" ref={dropdownRef}>
      {/* Universal Bell Button */}
      <button
        onClick={handleToggle}
        className="cht-p-2 cht-rounded-[12px] cht-text-gray-500 hover:cht-bg-gray-100 hover:cht-text-[#3e57da] cht-transition-all cht-relative active:cht-scale-95"
        title="Notifications"
        aria-label="View notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="cht-absolute -cht-top-0.5 -cht-right-0.5 cht-bg-red-500 cht-text-white cht-text-[9px] cht-font-black cht-min-w-[16px] cht-h-4 cht-px-1 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-shadow-sm cht-animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Floating Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 350, damping: 28 }}
            className="cht-absolute cht-right-0 cht-mt-3 cht-w-[360px] sm:cht-w-[390px] cht-bg-white/95 cht-backdrop-blur-2xl cht-border cht-border-gray-200/70 cht-rounded-[28px] cht-shadow-[0_20px_70px_rgba(0,0,0,0.12)] cht-z-[999] cht-overflow-hidden"
          >
            {/* Header */}
            <div className="cht-px-5 cht-py-4 cht-flex cht-items-center cht-justify-between cht-border-b cht-border-gray-100/80 cht-bg-white/60">
              <div className="cht-flex cht-items-center cht-gap-2">
                <h4 className="cht-text-xs cht-font-black cht-text-[#1e293b] cht-uppercase cht-tracking-wider">
                  Notifications
                </h4>
                {unreadCount > 0 && (
                  <span className="cht-px-2 cht-py-0.5 cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-text-[10px] cht-font-black cht-rounded-full">
                    {unreadCount} unread
                  </span>
                )}
              </div>

              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-[#3e57da] hover:cht-underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notification List */}
            <div className="cht-max-h-[420px] cht-overflow-y-auto cht-p-2 cht-space-y-1">
              {isLoading ? (
                <div className="cht-py-12 cht-text-center cht-space-y-2">
                  <div className="cht-w-6 cht-h-6 cht-border-2 cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full cht-animate-spin cht-mx-auto" />
                  <p className="cht-text-xs cht-font-bold cht-text-gray-400">Loading notifications...</p>
                </div>
              ) : notifications.length > 0 ? (
                notifications.map((item) => (
                  <NotificationItemRow
                    key={item.id}
                    item={item}
                    onMarkRead={handleMarkRead}
                    onNavigate={handleNavigate}
                  />
                ))
              ) : (
                <div className="cht-py-12 cht-text-center cht-space-y-2">
                  <div className="cht-w-12 cht-h-12 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-text-gray-300">
                    <Bell size={22} />
                  </div>
                  <p className="cht-text-xs cht-font-black cht-text-gray-700">All caught up!</p>
                  <p className="cht-text-[11px] cht-text-gray-400">No new alerts right now.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="cht-p-3.5 cht-bg-gray-50/70 cht-text-center cht-border-t cht-border-gray-100">
              <Link
                to={resolvedAllUrl}
                onClick={() => setIsOpen(false)}
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-[11px] cht-font-black cht-uppercase cht-tracking-widest cht-text-gray-500 hover:cht-text-[#3e57da] transition-colors"
              >
                <span>View Full Activity Center</span>
                <ExternalLink size={12} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UniversalNotificationDropdown;
